import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { User } from '@rossum/api-client/users';
import { Header, HeaderProps } from '@rossum/components/header/Header';
import { Button, Dialog, Tab, Tabs } from '@rossum/ui/material';
import { useResetPasswordByAdmin } from '../../../business/users/useResetPasswordByAdmin';
import { SettingsBreadcrumbs } from '../../../containers/Settings/SettingsBreadcrumbs';
import { getRoleName } from '../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../redux/modules/groups/selectors';
import { SSO } from '../../../redux/modules/users/values';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import { getUserName } from '../helpers';
import {
  userDetailsAccessPath,
  userDetailsBasePath,
  usersPath,
} from '../routes';
import { DeleteUserDialog } from './delete-user/DeleteUserDialog';
import { ResetUserPasswordDialog } from './ResetUserPasswordDialog';

const USER_PAGES_KEYS = ['access', 'info'] as const;

type OpenDialog = 'deleteAccount' | 'resetPassword' | null;

type UserHeaderProps = {
  pageKey: (typeof USER_PAGES_KEYS)[number];
  user: User;
  toggle: ReactNode;
} & Pick<HeaderProps, 'scrollableDivRef'>;

export const UserHeader = ({
  pageKey,
  user,
  toggle,
  ...rest
}: UserHeaderProps) => {
  const [showDialog, setShowDialog] = useState<OpenDialog>(null);

  const intl = useIntl();
  const history = useHistory();

  const { id } = user;
  const userName = getUserName(user);

  const groups = useSelector(groupsSelector);
  const role = getRoleName(user.groups, groups);

  const { mutate } = useResetPasswordByAdmin();

  const tabs = (
    <Tabs
      value={pageKey}
      onChange={(_, value) => {
        history.push(`${userDetailsBasePath(id)}/${value}`);
      }}
    >
      {USER_PAGES_KEYS.map(tab => (
        <Tab
          key={tab}
          label={intl.formatMessage({
            id: `containers.settings.users.${tab}`,
          })}
          data-cy={`user-header-tab-${tab}`}
          value={tab}
        />
      ))}
    </Tabs>
  );

  const getDialogContent = (open: OpenDialog) => {
    switch (open) {
      case 'deleteAccount':
        return (
          <DeleteUserDialog
            user={user}
            role={role}
            onDelete={() => history.push(usersPath())}
            onCancel={() => setShowDialog(null)}
          />
        );
      case 'resetPassword':
        return (
          <ResetUserPasswordDialog
            userName={userName}
            onReset={() =>
              mutate({
                email: user.email ?? user.username,
              })
            }
            onCancel={() => setShowDialog(null)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <HelmetComponent
        dynamicName={userName}
        translationKey="features.routes.pageTitles.settings.user"
      />
      <Header
        {...rest}
        breadcrumbs={
          <SettingsBreadcrumbs
            breadcrumbs={[
              {
                to: usersPath(),
                label: intl.formatMessage({
                  id: 'containers.settings.users.header.title',
                }),
              },
              {
                label: userName,
                to: userDetailsAccessPath(id),
              },
              {
                label: intl.formatMessage({
                  id: `containers.settings.users.${pageKey}`,
                }),
              },
            ]}
          />
        }
        tabs={tabs}
        title={intl.formatMessage({
          id: `containers.settings.users.user.${pageKey}`,
        })}
        description={null}
        buttons={[
          toggle,
          user?.authType !== SSO ? (
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => setShowDialog('resetPassword')}
              key="reset-password"
            >
              {intl.formatMessage({
                id: 'containers.settings.users.user.resetPassword',
              })}
            </Button>
          ) : null,
          <Button
            variant="contained"
            onClick={() => setShowDialog('deleteAccount')}
            key="delete-account"
          >
            {intl.formatMessage({
              id: 'containers.settings.users.user.deleteAccount',
            })}
          </Button>,
        ]}
      />
      <Dialog
        open={!!showDialog}
        slotProps={{
          paper: {
            sx: { width: 480 },
            elevation: 2,
          },
        }}
        onClose={() => setShowDialog(null)}
      >
        {getDialogContent(showDialog)}
      </Dialog>
    </>
  );
};
